import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import {
  AggregateCount,
  LineEquipmentCell, LineEquipmentCounts, LineEquipmentGroup, LineEquipmentItem,
  LineItem
} from "../../models/counts/lineCounts";
import CountsServiceName, {CountsService} from "../../services/counts/counts.service";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import {apsEnums} from "../../shared/dto/aps.enums";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import template from './equipmentGroupRejects.tpl.html';

let requires = [CountsServiceName, LoaderServiceName];

interface TabOptions {
  RejectRateGraph: string;
  DistributionGraph: string;
  MoldDistributionGraph: string;
}

export interface EquipmentGroupRejectsController {
  close(): void;
  setSelectedTab(tab: string): void;
  determineDistribution(): void;
  selectedTab: string;
  tabOptions: TabOptions;
  tabClass: string;
  timeZoneId: string;
}

export interface EquipmentGroupRejectsControllerData {
  equipmentGroup: LineEquipmentGroup;
  shopId: string;
  lines: LineItem[];
  detail: LineEquipmentCounts;
  timeZoneId: string;
}

export function openEquipmentGroupRejects(data: EquipmentGroupRejectsControllerData,
                                          $modal: mm.foundation.modal): angular.IPromise<any> {
  return $modal.open({
    template: template,
    controller: 'EquipmentGroupRejectsCtrl as vm',
    resolve: {
      data: () => data
    }
  }).result;
}

class EquipmentGroupRejectsControllerImpl implements EquipmentGroupRejectsController {
  private $modalInstance: mm.foundation.modalInstance;
  private $timeout: angular.ITimeoutService;
  private countsService: CountsService;
  private loaderService: LoaderService;
  tabClass: string = 'er-details';
  timeZoneId: string;

  equipmentGroup: LineEquipmentGroup;
  shopId: string;
  equipmentCount: number;
  detail: LineEquipmentCounts;
  selectedTab: string;
  tabOptions: TabOptions;
  lines: LineItem[];
  showDistribution: boolean;
  showMoldDistribution: boolean;
  distributionPretty: any[];
  rejectRateRange: string;
  private rejectRatePromise: angular.IPromise<any>;
  private $document: angular.IDocumentService;
  private dimpleFunctions: any;

  constructor(data: EquipmentGroupRejectsControllerData,
              $modalInstance: mm.foundation.modalInstance,
              countsService: CountsService,
              loaderService: LoaderService,
              $timeout: angular.ITimeoutService,
              $document: angular.IDocumentService) {

    this.equipmentGroup = data.equipmentGroup;
    this.$modalInstance = $modalInstance;
    this.countsService = countsService;
    this.loaderService = loaderService;
    this.$document = $document;
    this.$timeout = $timeout;
    this.rejectRateRange = 'LastHour';
    this.equipmentCount = Object.keys(this.equipmentGroup.equipment).length;
    this.detail = data.detail;
    this.shopId = data.shopId;
    this.timeZoneId = data.timeZoneId;

    if (data.equipmentGroup.lineLineEquipmentTabs && data.lines) {
      this.showDistribution = true;
      this.lines = data.lines;
    }
    if (_.some(this.equipmentGroup.types, t =>
      t === apsEnums.LineEquipmentType.CheckDetector
      || t === apsEnums.LineEquipmentType.HEYE)) {
      this.showMoldDistribution = true;
    }
    this.setTabClass();

    this.tabOptions = {
      RejectRateGraph: 'RejectRateGraph',
      DistributionGraph: 'DistributionGraph',
      MoldDistributionGraph: 'MoldDistributionGraph'
    };
    this.dimpleFunctions = {
      getToolTip: (e) => {
        return [
          e.aggField[0].substring(2),
          'Defect: ' + e.cx,
          'Reject Count: ' + e.cy
        ];
      }
    };

    if (this.equipmentGroup.hasRejects) {
      this.createRejectRatePromise();
    }
  }

  private setTabClass(): void {
    if (this.getTabCount() < 2) {
      this.tabClass = 'tab-title-muted';
    }
  }

  private getTabCount(): number {
    let tabCount = 0;
    if (this.equipmentGroup.hasRejects) {
      tabCount++;
    }
    if (this.detail) {
      if (this.detail.details && this.detail.details.length > 0) {
        tabCount++;
      }
      if (this.detail.tabs) {
        tabCount += this.detail.tabs.length;
      }
    }
    if (this.showDistribution) {
      tabCount++;
    }
    if (this.showMoldDistribution) {
      tabCount++;
    }
    return tabCount;
  }

  private createRejectRatePromise(): void {
    let now = moment().startOf('minute');
    if (now.minute() % 10 !== 0) {
      now = now.subtract('minute', now.minute() % 10);
    }
    let endTime = now.toDate();
    let startTime = this.rejectRateRange === 'LastHour'
      ? now.subtract('minute', 60).toDate()
      : now.subtract('hour', 24).toDate();
    this.rejectRatePromise = this.countsService.getGroupRejectCounts(this.shopId,
      this.equipmentGroup, startTime, endTime)
      .then(data => this.equipmentGroup.rejects = data);
  }

  updateRejectRateRange(): void {
    this.selectedTab = 'none';
    this.createRejectRatePromise();
    this.loaderService.loadingPromise(this.rejectRatePromise.then(() => {
      this.selectedTab = this.tabOptions.RejectRateGraph;
    }));
  }

  setSelectedTab(tab: string): void {
    this.$timeout(() => {
      if (tab === this.tabOptions.DistributionGraph) {
        this.determineDistribution();
      } else if (tab === this.tabOptions.RejectRateGraph) {
        this.loaderService.loadingPromise(this.rejectRatePromise.then(() => {
          this.selectedTab = tab;
        }));
        return;
      }

      this.selectedTab = tab;
    }, 50);
  }

  close(): void {
    this.$modalInstance.close();
  }

  determineDistribution(): void {
    this.distributionPretty = [];
    let equipmentIds = Object.keys(this.equipmentGroup.lineLineEquipmentTabs);

    equipmentIds.forEach(equipmentId => {
      let tabs = this.equipmentGroup.lineLineEquipmentTabs[equipmentId];
      let equipment: LineEquipmentItem = null;
      let line: apsViewModels.LineItem = null;
      for (let i = 0; i < this.lines.length; i++) {
        let targetLine = this.lines[i];
        equipment = _.find(targetLine.equipment, {id: equipmentId});
        if (equipment) {
          line = targetLine;
          break;
        }
      }

      let getCountTotal = (dataPointId: string, cell: LineEquipmentCell,
                           isAggregate: boolean): number => {
        if (!cell.count) {
          return 0;
        }

        if (!isAggregate) {
          if (!cell || !cell.count) {
            return;
          }
          return cell.count.count;
        }

        let count = cell.count as AggregateCount;
        let memberIndex = _.findIndex(count.memberCounts, { id: dataPointId });
        if (memberIndex < 0) {
          return 0;
        }

        return count.finalCounts[memberIndex].count;
      };

      tabs[0].rows.forEach(row => {
        if (row.isSpacer || row.name.toLowerCase().indexOf('camera') > -1) {
          return;
        }

        let cell = row.cells[0];
        let rowFromDetail = _.find(this.detail.tabs[0].rows, {name: row.name});
        let cellFromDetail = rowFromDetail.cells[0];

        let total = 0;
        let isRowAggregate = cell.dataPointIds && cell.dataPointIds.length > 0;
        let isDetailRowAggregate = cellFromDetail.dataPointIds && cellFromDetail.dataPointIds.length > 0;
        switch (isRowAggregate) {
          case true:
            row.cells[0].dataPointIds.forEach(dataPointId => {
              total += getCountTotal(dataPointId, cellFromDetail, isDetailRowAggregate);
            });
            break;

          default:
            total = getCountTotal(cell.dataPointId, cellFromDetail, isDetailRowAggregate);
            break;
        }
        if (total === 0) {
          return;
        }

        this.distributionPretty.push({
          Line: `${line.number}-${equipment.name}-${line.name}`,
          Defect: row.name,
          Count: total
        });
      });
    });

    this.$timeout(() =>
      this.$document.find('text.dimple-legend').each((i, e) => {
        e.innerHTML = e.innerHTML.substring(2);
      }));
  }
}

export default angular.module('aps.controller.equipmentGroupRejects', requires)
  .controller('EquipmentGroupRejectsCtrl', EquipmentGroupRejectsControllerImpl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/counts/equipmentGroupRejects.controller.ts